import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import api, { formatINR, pimg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";

const Cart = () => {
  const { items, setQty, removeItem, subtotal, grams } = useCart();
  const { settings } = useSettings();

  const perKg = settings?.delivery_per_kg ?? 70;
  const kg = grams ? Math.max(1, Math.ceil(grams / 1000)) : 1;
  const delivery = items.length ? kg * perKg : 0;
  const total = subtotal + delivery;

  if (items.length === 0)
    return (
      <div data-testid="cart-empty" className="mx-auto max-w-2xl px-5 py-24 text-center">
        <div className="h-20 w-20 rounded-full bg-mutedcream mx-auto flex items-center justify-center text-goldsoft">
          <ShoppingBag size={34} />
        </div>
        <h1 className="font-display text-3xl font-bold text-choco mt-6">Your cart is empty</h1>
        <p className="text-muted-foreground mt-2">Looks like you haven't added anything yet.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 mt-8 rounded-full bg-choco text-cream font-semibold px-7 py-3.5 hover:bg-chocodark transition-colors">
          Start Shopping <ArrowRight size={18} />
        </Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10 sm:py-14">
      <h1 className="font-display text-4xl font-bold text-choco mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.key} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 bg-white rounded-2xl border border-[#EFEBE1] p-4">
              <img src={pimg(it.image)} alt={it.name} className="h-24 w-24 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`/product/${it.product_id}`} className="font-display text-lg font-semibold text-choco hover:text-goldsoft line-clamp-2">{it.name}</Link>
                    {it.weight && <p className="text-xs text-muted-foreground">{it.weight}</p>}
                  </div>
                  <button data-testid={`cart-remove-${it.product_id}`} onClick={() => removeItem(it.key)} className="text-muted-foreground hover:text-destructive shrink-0 h-8 w-8 flex items-center justify-center"><Trash2 size={18} /></button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-[#E5DFD3] rounded-full">
                    <button data-testid={`cart-dec-${it.product_id}`} onClick={() => setQty(it.key, it.quantity - 1)} className="h-9 w-9 flex items-center justify-center text-choco hover:text-goldsoft"><Minus size={16} /></button>
                    <span className="w-8 text-center text-sm font-semibold">{it.quantity}</span>
                    <button data-testid={`cart-inc-${it.product_id}`} onClick={() => setQty(it.key, it.quantity + 1)} className="h-9 w-9 flex items-center justify-center text-choco hover:text-goldsoft"><Plus size={16} /></button>
                  </div>
                  <span className="font-display text-lg font-bold text-choco">{formatINR(it.price * it.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-[#EFEBE1] p-6 sticky top-24">
            <h2 className="font-display text-2xl font-bold text-choco mb-5">Order Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="cart-subtotal" className="font-semibold text-choco">{formatINR(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery ({kg} kg × {formatINR(perKg)})</span><span className="font-semibold text-choco">{formatINR(delivery)}</span></div>
              <p className="text-xs text-muted-foreground">Shipping {settings?.shipping_area || "across Karnataka"} · ₹{perKg}/kg · No COD</p>
              <div className="border-t border-[#EFEBE1] pt-3 flex justify-between items-center">
                <span className="font-semibold text-choco">Total</span>
                <span data-testid="cart-total" className="font-display text-2xl font-bold text-choco">{formatINR(total)}</span>
              </div>
            </div>
            <Link to="/checkout" data-testid="proceed-to-checkout" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark active:scale-95 transition-[background-color,transform]">
              Proceed to Checkout <ArrowRight size={18} />
            </Link>
            <Link to="/shop" className="mt-3 block text-center text-sm text-muted-foreground hover:text-choco">Continue shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
